import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { httpsCallable } from "firebase/functions";
import { patientsApi } from "./patientsApi";
import {
  functions,
  createLead,
  getLead,
  getLeads,
  updateLead,
  deleteLead,
  addLeadDevelopmentLog,
  convertLeadToPatient,
  restoreLead,
  hardDeleteLead,
  assignLead,
  setLeadLost,
  markLeadContacted,
  updateClinicLeadStatuses,
} from "../../firebase";

/** HttpsError → Hebrew message (server messages are Hebrew; SDK ones are not). Never leaks CK codes. */
const toWebhookError = (error, fallback) => {
  const msg = typeof error?.message === "string" ? error.message : "";
  if (msg === "TRIAL_EXPIRED") return { message: "תקופת הניסיון הסתיימה. יש לשדרג את המסלול כדי להמשיך.", code: error?.code };
  return { message: /[֐-׿]/.test(msg) ? msg : fallback, code: error?.code };
};

export const leadsApi = createApi({
  reducerPath: "leadsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Lead", "LeadsWebhook"],
  endpoints: (builder) => ({
    // ============================================
    // LEADS
    // ============================================

    // Leads v2: clinic-wide list. `therapistId` / `status` / `excludeConverted`
    // are accepted for call-site compatibility but filtering happens in the UI.
    // Use ONE arg shape everywhere (`{ clinicId }`) so the app shares a single
    // cache entry. `includeDeleted` is for the admin archive only.
    getLeads: builder.query({
      async queryFn({ clinicId, therapistId, includeDeleted = false }) {
        try {
          const leads = await getLeads(clinicId, therapistId, { includeDeleted });
          return { data: leads };
        } catch (error) {
          console.error("[CK-7800] Error fetching leads:", {
            errorCode: "CK-7800",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Lead", id })),
              { type: "Lead", id: "LIST" },
            ]
          : [{ type: "Lead", id: "LIST" }],
    }),

    // Get single lead
    getLead: builder.query({
      async queryFn({ clinicId, leadId }) {
        try {
          const lead = await getLead(clinicId, leadId);
          return { data: lead };
        } catch (error) {
          console.error("[CK-7801] Error fetching lead:", {
            errorCode: "CK-7801",
            errorMessage: error.message,
            clinicId,
            leadId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { leadId }) => [{ type: "Lead", id: leadId }],
    }),

    // Create lead
    createLead: builder.mutation({
      async queryFn({ clinicId, leadData, therapistId, therapistName, owner = null }) {
        try {
          const lead = await createLead(clinicId, leadData, therapistId, therapistName, owner);
          return { data: lead };
        } catch (error) {
          console.error("[CK-7802] Error creating lead:", {
            errorCode: "CK-7802",
            errorMessage: error.message,
            clinicId,
            therapistId,
            leadData: { name: leadData?.name },
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Lead", id: "LIST" }],
    }),

    // Update lead
    updateLead: builder.mutation({
      async queryFn({ clinicId, leadId, leadData, therapistId, therapistName, owner = null }) {
        try {
          const lead = await updateLead(clinicId, leadId, leadData, therapistId, therapistName, owner);
          return { data: lead };
        } catch (error) {
          console.error("[CK-7803] Error updating lead:", {
            errorCode: "CK-7803",
            errorMessage: error.message,
            clinicId,
            leadId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { leadId }) => [
        { type: "Lead", id: leadId },
        { type: "Lead", id: "LIST" },
      ],
    }),

    // Delete lead (Leads v2: soft delete)
    deleteLead: builder.mutation({
      async queryFn({ clinicId, leadId, therapistId, therapistName, leadName, reason = null }) {
        try {
          await deleteLead(clinicId, leadId, therapistId, therapistName, leadName, reason);
          return { data: leadId };
        } catch (error) {
          console.error("[CK-7804] Error deleting lead:", {
            errorCode: "CK-7804",
            errorMessage: error.message,
            clinicId,
            leadId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Lead", id: "LIST" }],
    }),

    // Add development log to lead
    addLeadDevelopmentLog: builder.mutation({
      async queryFn({ clinicId, leadId, content, authorId, authorName }) {
        try {
          const lead = await addLeadDevelopmentLog(
            clinicId,
            leadId,
            content,
            authorId,
            authorName
          );
          return { data: lead };
        } catch (error) {
          console.error("[CK-7805] Error adding lead development log:", {
            errorCode: "CK-7805",
            errorMessage: error.message,
            clinicId,
            leadId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { leadId }) => [
        { type: "Lead", id: leadId },
        { type: "Lead", id: "LIST" },
      ],
    }),

    // Convert lead to patient
    convertLeadToPatient: builder.mutation({
      async queryFn(
        { clinicId, leadId, therapistId, therapistName, ownerTherapistId = null },
        { dispatch }
      ) {
        try {
          const result = await convertLeadToPatient(
            clinicId,
            leadId,
            therapistId,
            therapistName,
            ownerTherapistId
          );
          // Cross-API: a new patient doc was created
          dispatch(
            patientsApi.util.invalidateTags([{ type: "Patients", id: "LIST" }])
          );
          return { data: result };
        } catch (error) {
          console.error("[CK-7806] Error converting lead to patient:", {
            errorCode: "CK-7806",
            errorMessage: error.message,
            clinicId,
            leadId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { leadId }) => [
        { type: "Lead", id: leadId },
        { type: "Lead", id: "LIST" },
      ],
    }),

    // ============================================
    // LEADS v2 (leads_plan.md §6.2) — CK-7840+
    // ============================================

    // Restore a soft-deleted lead (admin)
    restoreLead: builder.mutation({
      async queryFn({ clinicId, leadId, therapistId, therapistName, leadName }) {
        try {
          const lead = await restoreLead(clinicId, leadId, therapistId, therapistName, leadName);
          return { data: lead };
        } catch (error) {
          console.error("[CK-7840] Error restoring lead:", {
            errorCode: "CK-7840",
            errorMessage: error.message,
            clinicId,
            leadId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { leadId }) => [
        { type: "Lead", id: leadId },
        { type: "Lead", id: "LIST" },
      ],
    }),

    // Permanently delete (admin, archive only)
    hardDeleteLead: builder.mutation({
      async queryFn({ clinicId, leadId, therapistId, therapistName, leadName }) {
        try {
          await hardDeleteLead(clinicId, leadId, therapistId, therapistName, leadName);
          return { data: leadId };
        } catch (error) {
          console.error("[CK-7841] Error hard-deleting lead:", {
            errorCode: "CK-7841",
            errorMessage: error.message,
            clinicId,
            leadId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Lead", id: "LIST" }],
    }),

    // Assign / unassign (assignee = { therapistId: null } ⇒ pool)
    assignLead: builder.mutation({
      async queryFn({ clinicId, leadId, assignee, therapistId, therapistName, leadName }) {
        try {
          const lead = await assignLead(clinicId, leadId, assignee, therapistId, therapistName, leadName);
          return { data: lead };
        } catch (error) {
          console.error("[CK-7842] Error assigning lead:", {
            errorCode: "CK-7842",
            errorMessage: error.message,
            clinicId,
            leadId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { leadId }) => [
        { type: "Lead", id: leadId },
        { type: "Lead", id: "LIST" },
      ],
    }),

    // Mark lost with reason
    setLeadLost: builder.mutation({
      async queryFn({ clinicId, leadId, lostReason, lostNote, therapistId, therapistName, leadName }) {
        try {
          const lead = await setLeadLost(clinicId, leadId, { lostReason, lostNote }, therapistId, therapistName, leadName);
          return { data: lead };
        } catch (error) {
          console.error("[CK-7843] Error marking lead lost:", {
            errorCode: "CK-7843",
            errorMessage: error.message,
            clinicId,
            leadId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { leadId }) => [
        { type: "Lead", id: leadId },
        { type: "Lead", id: "LIST" },
      ],
    }),

    // "סמן שנוצר קשר" (+ optional next follow-up)
    markLeadContacted: builder.mutation({
      async queryFn({ clinicId, leadId, nextFollowUpAt, therapistId, therapistName, leadName }) {
        try {
          const lead = await markLeadContacted(clinicId, leadId, nextFollowUpAt, therapistId, therapistName, leadName);
          return { data: lead };
        } catch (error) {
          console.error("[CK-7844] Error marking lead contacted:", {
            errorCode: "CK-7844",
            errorMessage: error.message,
            clinicId,
            leadId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { leadId }) => [
        { type: "Lead", id: leadId },
        { type: "Lead", id: "LIST" },
      ],
    }),

    // Clinic-wide status list (the caller refreshes the clinic profile in authSlice)
    updateClinicLeadStatuses: builder.mutation({
      async queryFn({ clinicId, statuses, therapistId, therapistName }) {
        try {
          const saved = await updateClinicLeadStatuses(clinicId, statuses, therapistId, therapistName);
          return { data: saved };
        } catch (error) {
          console.error("[CK-7845] Error updating clinic lead statuses:", {
            errorCode: "CK-7845",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Lead", id: "LIST" }],
    }),

    // ============================================
    // LEADS v2 — inbound webhook settings (leads_plan.md §8) — callables, CK-7850+
    // ============================================
    getLeadsWebhookConfig: builder.query({
      async queryFn({ clinicId }) {
        try {
          const res = await httpsCallable(functions, "getLeadsWebhookConfig")({ clinicId });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7850] Error loading leads webhook config:", {
            errorCode: "CK-7850",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: toWebhookError(error, "טעינת הגדרות הקליטה נכשלה") };
        }
      },
      providesTags: [{ type: "LeadsWebhook", id: "CONFIG" }],
    }),
    createLeadsApiKey: builder.mutation({
      async queryFn({ clinicId, label, defaultTherapistId, defaultReferralSource }) {
        try {
          const res = await httpsCallable(functions, "createLeadsApiKey")({ clinicId, label, defaultTherapistId, defaultReferralSource });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7851] Error creating leads API key:", {
            errorCode: "CK-7851",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: toWebhookError(error, "יצירת המפתח נכשלה") };
        }
      },
      invalidatesTags: [{ type: "LeadsWebhook", id: "CONFIG" }],
    }),
    updateLeadsApiKey: builder.mutation({
      async queryFn({ clinicId, keyId, label, defaultTherapistId, defaultReferralSource }) {
        try {
          const res = await httpsCallable(functions, "updateLeadsApiKey")({ clinicId, keyId, label, defaultTherapistId, defaultReferralSource });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7852] Error updating leads API key:", {
            errorCode: "CK-7852",
            errorMessage: error.message,
            clinicId,
            keyId,
            timestamp: new Date().toISOString(),
          });
          return { error: toWebhookError(error, "עדכון המפתח נכשל") };
        }
      },
      invalidatesTags: [{ type: "LeadsWebhook", id: "CONFIG" }],
    }),
    rotateLeadsApiKey: builder.mutation({
      async queryFn({ clinicId, keyId }) {
        try {
          const res = await httpsCallable(functions, "rotateLeadsApiKey")({ clinicId, keyId });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7853] Error rotating leads API key:", {
            errorCode: "CK-7853",
            errorMessage: error.message,
            clinicId,
            keyId,
            timestamp: new Date().toISOString(),
          });
          return { error: toWebhookError(error, "החלפת המפתח נכשלה") };
        }
      },
      invalidatesTags: [{ type: "LeadsWebhook", id: "CONFIG" }],
    }),
    revokeLeadsApiKey: builder.mutation({
      async queryFn({ clinicId, keyId }) {
        try {
          const res = await httpsCallable(functions, "revokeLeadsApiKey")({ clinicId, keyId });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7854] Error revoking leads API key:", {
            errorCode: "CK-7854",
            errorMessage: error.message,
            clinicId,
            keyId,
            timestamp: new Date().toISOString(),
          });
          return { error: toWebhookError(error, "ביטול המפתח נכשל") };
        }
      },
      invalidatesTags: [{ type: "LeadsWebhook", id: "CONFIG" }],
    }),
    setLeadsWebhookPaused: builder.mutation({
      async queryFn({ clinicId, paused }) {
        try {
          const res = await httpsCallable(functions, "setLeadsWebhookPaused")({ clinicId, paused });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7855] Error toggling leads webhook pause:", {
            errorCode: "CK-7855",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: toWebhookError(error, "עדכון ההשהיה נכשל") };
        }
      },
      invalidatesTags: [{ type: "LeadsWebhook", id: "CONFIG" }],
    }),
    sendLeadsWebhookTest: builder.mutation({
      async queryFn({ clinicId, keyId }) {
        try {
          const res = await httpsCallable(functions, "sendLeadsWebhookTest")({ clinicId, keyId });
          return { data: res.data };
        } catch (error) {
          console.error("[CK-7856] Error sending leads webhook test:", {
            errorCode: "CK-7856",
            errorMessage: error.message,
            clinicId,
            keyId,
            timestamp: new Date().toISOString(),
          });
          return { error: toWebhookError(error, "שליחת הבדיקה נכשלה") };
        }
      },
      invalidatesTags: [{ type: "LeadsWebhook", id: "CONFIG" }, { type: "Lead", id: "LIST" }],
    }),
  }),
});

export const {
  useGetLeadsQuery,
  useGetLeadQuery,
  useCreateLeadMutation,
  useUpdateLeadMutation,
  useDeleteLeadMutation,
  useAddLeadDevelopmentLogMutation,
  useConvertLeadToPatientMutation,
  useRestoreLeadMutation,
  useHardDeleteLeadMutation,
  useAssignLeadMutation,
  useSetLeadLostMutation,
  useMarkLeadContactedMutation,
  useUpdateClinicLeadStatusesMutation,
  useGetLeadsWebhookConfigQuery,
  useCreateLeadsApiKeyMutation,
  useUpdateLeadsApiKeyMutation,
  useRotateLeadsApiKeyMutation,
  useRevokeLeadsApiKeyMutation,
  useSetLeadsWebhookPausedMutation,
  useSendLeadsWebhookTestMutation,
} = leadsApi;

